import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiClient, type ApiError, type Paginated } from '@/core/api';

const HRM_BASE = '/hrm/bank-accounts/';

/** Payout rail — mirrors backend hrm.constants.BankAccountMethod. */
export type BankAccountMethod =
  | 'iban'
  | 'sepa'
  | 'swift'
  | 'wise'
  | 'payoneer'
  | 'local'
  | 'other';

export const BANK_ACCOUNT_METHODS: { value: BankAccountMethod; label: string }[] = [
  { value: 'iban', label: 'IBAN' },
  { value: 'sepa', label: 'SEPA (EUR / IBAN)' },
  { value: 'swift', label: 'SWIFT / BIC' },
  { value: 'wise', label: 'Wise' },
  { value: 'payoneer', label: 'Payoneer' },
  { value: 'local', label: 'Local bank transfer' },
  { value: 'other', label: 'Other' },
];

export const bankAccountMethodLabel = (m: string): string =>
  BANK_ACCOUNT_METHODS.find((x) => x.value === m)?.label ?? m;

/** Mirrors EmployeeBankAccountReadSerializer. */
export interface EmployeeBankAccount {
  id: number;
  public_id: string;
  employee: number;
  label: string;
  method: BankAccountMethod;
  account_holder: string;
  bank_name: string;
  bank_address: string;
  iban: string;
  swift_bic: string;
  account_number: string;
  routing_number: string;
  /** Correspondent bank for cross-border wires — printed on the staff invoice. */
  intermediary_bank_name: string;
  intermediary_swift_bic: string;
  intermediary_account: string;
  email: string;
  currency: string;
  country: string;
  is_default: boolean;
  notes: string;
  created: string;
  modified: string;
}

/** Write payload shared by cabinet + HRM (HRM additionally sends `employee`). */
export interface BankAccountInput {
  label: string;
  method: BankAccountMethod;
  account_holder: string;
  bank_name: string;
  bank_address: string;
  iban: string;
  swift_bic: string;
  account_number: string;
  routing_number: string;
  /** Correspondent bank for cross-border wires — printed on the staff invoice. */
  intermediary_bank_name: string;
  intermediary_swift_bic: string;
  intermediary_account: string;
  email: string;
  currency: string;
  country: string;
  is_default: boolean;
  notes: string;
}

// ---- HRM endpoint (HR / Founder / Super Admin manage any employee's) -------
export async function listEmployeeBankAccounts(
  employee: number,
): Promise<Paginated<EmployeeBankAccount>> {
  const { data } = await apiClient.get<Paginated<EmployeeBankAccount>>(HRM_BASE, {
    params: { employee },
  });
  return data;
}

export async function createEmployeeBankAccount(
  input: BankAccountInput & { employee: number },
): Promise<EmployeeBankAccount> {
  const { data } = await apiClient.post<EmployeeBankAccount>(HRM_BASE, input);
  return data;
}

export async function updateEmployeeBankAccount(
  id: string | number,
  input: Partial<BankAccountInput>,
): Promise<EmployeeBankAccount> {
  const { data } = await apiClient.patch<EmployeeBankAccount>(`${HRM_BASE}${id}/`, input);
  return data;
}

export async function deleteEmployeeBankAccount(id: string | number): Promise<void> {
  await apiClient.delete(`${HRM_BASE}${id}/`);
}

const bankKeys = {
  byEmployee: (employeeId: number) => ['hrm', 'bank-accounts', employeeId] as const,
};

export function useEmployeeBankAccounts(employeeId: number) {
  return useQuery({
    queryKey: bankKeys.byEmployee(employeeId),
    queryFn: () => listEmployeeBankAccounts(employeeId),
    enabled: Number.isInteger(employeeId) && employeeId > 0,
  });
}

export function useCreateEmployeeBankAccount(employeeId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BankAccountInput) =>
      createEmployeeBankAccount({ ...input, employee: employeeId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankKeys.byEmployee(employeeId) });
      toast.success('Bank account added');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateEmployeeBankAccount(employeeId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | number; input: Partial<BankAccountInput> }) =>
      updateEmployeeBankAccount(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankKeys.byEmployee(employeeId) });
      toast.success('Bank account updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteEmployeeBankAccount(employeeId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => deleteEmployeeBankAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankKeys.byEmployee(employeeId) });
      toast.success('Bank account removed');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
